import { useState } from 'react';
import { Box } from '@mui/material';
import Modal from '../../../components/common/Modal';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { useGlobalStore } from 'store/globalStore';
import { useNavigate } from 'react-router-dom';
import {
  Backdrop,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import { CRUD_STATES } from 'constants/app';
import { adminAPI, videoAPI } from 'services/admin';
import { useNotification } from 'hooks/useNotification';
import awsExports from '../../../aws-exports';

export const CustomActionsBox = ({ data }) => {
  const { id, status, version, title, mediaId } = data;
  const { setNotify } = useNotification();
  const { setRefreshData, refreshData } = useGlobalStore();
  const [confirmDialogAction, setConfirmDialogAction] = useState({
    isOpen: false,
    title: '',
    subTitle: '',
  });
  const [copyMenuState, setCopyMenuState] = useState({
    anchorEl: null,
    mediaId: '',
  });
  const { showConfirmationButton } = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const WEB_PLAYER_BASE_URL = awsExports.ENDPOINT_PLAYER_WEB;

  const buildEmbedIframe = value =>
    `<iframe src="${WEB_PLAYER_BASE_URL}/embed-public/${value}" width="100%" height="100%"
   frameborder="0" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen 
   referrerpolicy="strict-origin-when-cross-origin">
   </iframe>`;

  const copyTextToClipboard = async (text, successMessage) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      setNotify({
        isOpen: true,
        message: successMessage,
        type: 'success',
        title: 'Sucesso!',
      });
    } catch (error) {
      setNotify({
        isOpen: true,
        message: 'Não foi possível copiar para a área de transferência.',
        type: 'error',
        title: 'Ops! Algo deu errado...',
      });
    }
  };

  const openCopyMenu = event => {
    event.stopPropagation();

    if (!mediaId) {
      setNotify({
        isOpen: true,
        message: 'Este vídeo não possui mediaId disponível para cópia.',
        type: 'warning',
        title: 'Atenção',
      });
      return;
    }

    setCopyMenuState({
      anchorEl: event.currentTarget,
      mediaId,
    });
  };

  const closeCopyMenu = () => {
    setCopyMenuState({
      anchorEl: null,
      mediaId: '',
    });
  };

  const handleCopyMediaId = async () => {
    await copyTextToClipboard(
      copyMenuState.mediaId,
      'mediaId copiado com sucesso!'
    );
    closeCopyMenu();
  };

  const handleCopyIframe = async () => {
    await copyTextToClipboard(
      buildEmbedIframe(copyMenuState.mediaId),
      'Iframe copiado com sucesso!'
    );
    closeCopyMenu();
  };

  const handleEditAndViewVideo = action => {
    setIsLoading(true);
    if (
      action.action === CRUD_STATES.EDIT &&
      (action.status === 'EXPIRED' || action.status === 'PUBLISHED')
    ) {
      const path = `/${action?.id}/versions`;

      videoAPI
        .post(path)
        .then(resp => {
          setIsLoading(false);
          navigate(`/upload-video/${action?.id}/${resp.version}`, {
            state: {
              action: action.action,
              id: action?.id,
              version: action?.version || 1,
            },
          });
        })
        .catch(error => {
          console.log(error);
          setIsLoading(false);
          setConfirmDialogAction({ isOpen: false });
          const errorJson = error.request.response;
          const parseJson = JSON.parse(errorJson);

          setNotify({
            isOpen: true,
            message: parseJson.message,
            type: 'error',
            title: 'Ops! Algo deu errado...',
          });
        });
    } else {
      setIsLoading(true);
      navigate(`/upload-video/${action?.id}/${action?.version}`, {
        state: {
          action: action.action,
          id: action.id,
          version: action?.version || 1,
        },
      });
    }
  };

  const handleRemoveVideo = async (videoId, version, title) => {
    setIsLoading(true);
    try {
      const path = `/admin/videos/${videoId}/versions/${version}`;
      await adminAPI.del(path);
      setTimeout(() => {
        setConfirmDialogAction({ isOpen: false });
        setNotify({
          isOpen: true,
          message: `O video "${title}" foi removido com sucesso!`,
          type: 'success',
          title: 'Sucesso!',
        });
        setIsLoading(false);
        setRefreshData(refreshData + 1);
      }, 4000);
    } catch (error) {
      console.log(error);
      setIsLoading(false);
      setConfirmDialogAction({ isOpen: false });
      const errorJson = error.request.response;
      const parseJson = JSON.parse(errorJson);
      setNotify({
        isOpen: true,
        message: parseJson.message,
        type: 'error',
        title: 'Ops! Algo deu errado...',
      });
      console.log(error);
    }
  };

  const handleArchiveVideo = async (videoId, version, title) => {
    setIsLoading(true);
    try {
      const path = `/admin/videos/${videoId}/versions/${version}/status/archived`;
      await adminAPI.patch(path);
      setTimeout(() => {
        setConfirmDialogAction({ isOpen: false });
        setNotify({
          isOpen: true,
          message: `O video "${title}" arquivado com sucesso!`,
          type: 'success',
          title: 'Sucesso!',
        });
        setIsLoading(false);
        setRefreshData(refreshData + 1);
      }, 4000);
    } catch (error) {
      setIsLoading(false);
      setConfirmDialogAction({ isOpen: false });
      const errorJson = error.request.response;
      const parseJson = JSON.parse(errorJson);
      setNotify({
        isOpen: true,
        message: parseJson.message,
        type: 'error',
        title: 'Ops! Algo deu errado...',
      });
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'row' }}>
      <Box sx={{ cursor: 'pointer', width: '35px' }}>
        {status === 'DRAFT' ? (
          ''
        ) : status === 'FAILED' ? (
          ''
        ) : (
          <img
            onClick={() => {
              handleEditAndViewVideo({
                action: CRUD_STATES.VIEW,
                id: id,
                version: version,
                status: status,
              });
            }}
            className="eye-icon"
            src="/img/videosDataTable/eye.svg"
            alt="Visualizar"
            style={{ width: '25px', height: '25px' }}
          />
        )}
      </Box>
      <Box
        onClick={() => {
          handleEditAndViewVideo({
            action: CRUD_STATES.EDIT,
            id: id,
            version: version,
            status: status,
          });
        }}
        sx={{ cursor: 'pointer', width: '35px' }}>
        {status === 'ARCHIVED' ? (
          ''
        ) : status === 'TRANSCODING' ? (
          ''
        ) : (
          <img
            className="archive-icon"
            src="/img/videosDataTable/editpencil.svg"
            alt="editar"
            style={{ width: '25px', height: '25px' }}
          />
        )}
      </Box>
      <Box sx={{ cursor: 'pointer', width: '35px' }}>
        {status === 'ARCHIVED' ? (
          ''
        ) : status === 'TRANSCODING' ? (
          ''
        ) : status === 'DRAFT' ? (
          <img
            className="trash-icon"
            src="/img/videosDataTable/trash.svg"
            onClick={() =>
              setConfirmDialogAction({
                isOpen: true,
                title: (
                  <div className="modalTitleText">
                    Remover o título "{title}"?
                  </div>
                ),
                subTitle: (
                  <div className="modalBodyText">
                    Você está removendo o título "{title}" permanentemente.
                  </div>
                ),
                children: (
                  <div className="modalRemoveItem">
                    <DeleteOutlineOutlinedIcon color="error" fontSize="12px" />
                  </div>
                ),
                confirmTextButton: 'Remover',
                color: 'error',
                onConfirm: () => {
                  handleRemoveVideo(id, version, title);
                },
                showConfirmationButton: { showConfirmationButton },
                closeTextButton: 'Cancelar',
              })
            }
            alt="Remover"
            style={{ width: '25px', height: '25px' }}
          />
        ) : status === 'FAILED' ? (
          <img
            className="trash-icon"
            src="/img/videosDataTable/trash.svg"
            onClick={() =>
              setConfirmDialogAction({
                isOpen: true,
                title: (
                  <div className="modalTitleText">
                    Remover o título "{title}"?
                  </div>
                ),
                subTitle: (
                  <div className="modalBodyText">
                    Você está removendo o título "{title}" permanentemente.
                  </div>
                ),
                children: (
                  <div className="modalRemoveItem">
                    <DeleteOutlineOutlinedIcon color="error" fontSize="12px" />
                  </div>
                ),
                confirmTextButton: 'Remover',
                color: 'error',
                onConfirm: () => {
                  handleRemoveVideo(id, version);
                },
                showConfirmationButton: { showConfirmationButton },
                closeTextButton: 'Cancelar',
              })
            }
            alt="Remover"
            style={{ width: '25px', height: '25px' }}
          />
        ) : (
          <img
            className="archive-icon"
            src="/img/videosDataTable/archive.svg"
            onClick={() =>
              setConfirmDialogAction({
                isOpen: true,
                title: (
                  <div className="modalTitleText">
                    Arquivar o título "{title}"?
                  </div>
                ),
                subTitle: (
                  <div className="modalBodyText">
                    Você irá arquivar o título "{title}" permanentemente.
                  </div>
                ),
                children: (
                  <div className="modalResendSentEmail">
                    <InfoOutlinedIcon color="primary" fontSize="12px" />
                  </div>
                ),
                confirmTextButton: 'Arquivar',
                color: 'primary',
                onConfirm: () => {
                  handleArchiveVideo(id, version, title);
                },
                showConfirmationButton: { showConfirmationButton },
                closeTextButton: 'Cancelar',
              })
            }
            alt="Arquivar"
            style={{ width: '25px', height: '25px' }}
          />
        )}
      </Box>
      <Box sx={{ cursor: 'pointer', width: '35px' }}>
        {status === 'DRAFT' ? (
          ''
        ) : status === 'FAILED' ? (
          ''
        ) : status === 'ARCHIVED' ? (
          ''
        ) : status === 'TRANSCODING' ? (
          ''
        ) : status === 'EXPIRED' ? (
          ''
        ) : (
          <IconButton
            size="small"
            onClick={openCopyMenu}
            aria-label="Copiar mediaId ou iframe"
            title="Copiar mediaId ou iframe"
            sx={{ p: '2px' }}>
            <ContentCopyOutlinedIcon sx={{ fontSize: 20, color: '#6F6B7D' }} />
          </IconButton>
        )}
      </Box>
      <Modal
        confirmDialogAction={confirmDialogAction}
        setConfirmDialogAction={setConfirmDialogAction}
      />
      <Menu
        anchorEl={copyMenuState.anchorEl}
        open={Boolean(copyMenuState.anchorEl)}
        onClose={closeCopyMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
        <MenuItem onClick={handleCopyMediaId}>Copiar mediaId</MenuItem>
        <MenuItem onClick={handleCopyIframe}>Copiar iframe</MenuItem>
      </Menu>
      <Backdrop
        open={isLoading}
        style={{ zIndex: 9999 }}
        sx={{ color: '#fff' }}>
        <CircularProgress color="inherit" size={60} />
      </Backdrop>
    </div>
  );
};
